<template>
    <hox-modal class="importExportModal" @close="close">
        <template #header>Import/Export</template>

        <div>
            <Tabs v-model="activeTab" class="importExportModal__navigation">
                <TabPane label="Selection" name="selection">
                    <ImportExportLibraryModalSelectionTab
                        @exportTemplate="onExportTemplate"
                        @change="onSelectionChange"
                    />
                </TabPane>

                <TabPane label="Metadata" name="metadata">
                    <ImportExportLibraryModalMetadataTab
                        :metadata-selection-count="metadataSelectionCount"
                        :is-exporting-metadata="isExportingMetadata"
                        @exportMetadata="onExportMetadata"
                        @change="onMetadataChange"
                    />
                </TabPane>
            </Tabs>
        </div>

        <template #footer>
            <div class="share-preview-modal__footer-button-wrapper">
                <Button data-testid="import-export-modal__close-button" @click="close">Close</Button>
                <template v-if="activeTab === 'selection' && selectionState.isReady">
                    <Button
                        class="importExportModal__button"
                        type="primary"
                        data-testid="import-export-modal__apply-selection-button"
                        @click="applySelection"
                    >
                        Apply selection
                    </Button>
                    <Button
                        class="importExportModal__button"
                        data-testid="import-export-modal__download-button"
                        type="primary"
                        :disabled="isDownloadDeliveriesLoading"
                        :loading="isDownloadDeliveriesLoading"
                        @click="download"
                    >
                        Download
                    </Button>
                </template>
                <Button
                    v-if="activeTab === 'metadata' && metadataState.isReady"
                    class="importExportModal__button"
                    type="primary"
                    data-testid="import-export-modal__apply-metadata-button"
                    :disabled="isApplyingMetadata"
                    :loading="isApplyingMetadata"
                    @click="applyMetadata"
                >
                    Apply changes
                </Button>
            </div>
        </template>
    </hox-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";

import ImportExportLibraryModalMetadataTab from "./ImportExportLibraryModalMetadataTab.vue";
import ImportExportLibraryModalSelectionTab from "./ImportExportLibraryModalSelectionTab.vue";
import type { MetadataTabState, SelectionTabState } from "../../../types/importExportTypes.ts";

interface ImportExportLibraryModalData {
    activeTab: string;
    importExportTracked: boolean;
    selectionState: SelectionTabState;
    metadataState: MetadataTabState;
}

export default defineComponent({
    name: "ImportExportLibraryModal",

    components: {
        ImportExportLibraryModalMetadataTab,
        ImportExportLibraryModalSelectionTab
    },

    props: {
        isDownloadDeliveriesLoading: {
            type: Boolean,
            default: false
        },
        isApplyingMetadata: {
            type: Boolean,
            default: false
        },
        isExportingMetadata: {
            type: Boolean,
            default: false
        },
        metadataSelectionCount: {
            type: Number,
            default: 0
        }
    },

    emits: ["close", "applySelection", "applyMetadata", "exportTemplate", "download", "exportMetadata"],

    setup() {
        const ga4 = useGA4Tracking();

        return { ga4 };
    },

    data(): ImportExportLibraryModalData {
        return {
            activeTab: "selection",
            importExportTracked: false,
            selectionState: { isReady: false, validRows: [], reportingLabels: [] },
            metadataState: { isReady: false, rows: [] }
        };
    },

    created() {
        this.ga4.trackFeatureEvent({
            featureEvent: GA4.EVENT_TYPES.FEATURE_ACTIVATE,
            featureCategory: GA4.FEATURE_CATEGORIES.IMPORT_EXPORT,
            featureContext: GA4.FEATURE_CONTEXTS.IMPORT_EXPORT
        });
    },

    methods: {
        onSelectionChange(state: SelectionTabState) {
            this.selectionState = state;
            if (state.isReady) {
                this.importExportTracked = true;
            }
        },

        onMetadataChange(state: MetadataTabState) {
            this.metadataState = state;
        },

        onExportTemplate() {
            this.$emit("exportTemplate");
        },

        onExportMetadata() {
            this.$emit("exportMetadata");
        },

        applySelection() {
            this.$emit("applySelection", this.selectionState.validRows, this.selectionState.reportingLabels);
        },

        download() {
            this.$emit("download", this.selectionState.validRows, this.selectionState.reportingLabels);
        },

        applyMetadata() {
            this.$emit("applyMetadata", this.metadataState.rows);
        },

        close() {
            if (!this.importExportTracked) {
                this.importExportTracked = true;
                this.ga4.trackFeatureEvent({
                    featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
                    featureCategory: GA4.FEATURE_CATEGORIES.IMPORT_EXPORT,
                    featureContext: GA4.FEATURE_CONTEXTS.CLOSE_IMPORT_EXPORT
                });
            }
            this.$emit("close");
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.importExportModal {
    &__navigation {
        margin-bottom: $spacing;
    }

    &__howto {
        &-list {
            padding-left: $spacing;
        }
    }

    &__files {
        padding-left: $spacing;
        margin-bottom: $spacing;
    }

    &__upload-result {
        margin-top: $spacing;
    }

    &__upload-results-error-list {
        padding-left: $spacing;
        word-break: break-word;
    }

    &__upload-controls {
        display: flex;
        width: 100%;
        justify-content: space-between;
        align-items: center;

        .labeled-switch {
            flex: 0;

            &__label {
                white-space: nowrap;
            }
        }
    }
}

.importExportModal__button:not(:first-child) {
    margin-left: $spacing-small;
}

.importExportModal__upload-result__file {
    margin-top: $spacing-small;
}

.importExportModal__number-of-deliverables {
    margin-bottom: $spacing !important;
}

.importExportModal__upload-metadata-results {
    margin-top: $spacing !important;
}
</style>
